import {
  forwardRef,
  HTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'

type Props = {
  placeholderProps: {
    description: string
  } & HTMLAttributes<HTMLSpanElement> & { [key: `data-${string}`]: string }
  valueProps: {
    dataDescription: string
  } & HTMLAttributes<HTMLSpanElement> & { [key: `data-${string}`]: string }
} & HTMLAttributes<HTMLSpanElement> & { [key: `data-${string}`]: string }

export const PlaceholderSpan = forwardRef<HTMLSpanElement, Props>(
  (
    {
      placeholderProps: { description, ...placeholderProps },
      valueProps: { dataDescription, ...valueProps },
      ...props
    },
    ref
  ) => {
    const localRef = useRef<HTMLSpanElement>(null)
    const placeholderRef = useRef<HTMLSpanElement>(null)
    const editableRef = useRef<HTMLSpanElement>(null)

    const togglePlaceholderDisplay = (display: 'block' | 'none') => {
      const element = placeholderRef.current
      if (element) {
        element.style.display = display
      }
    }

    const observer = new MutationObserver(() => {
      const editable = editableRef.current
      const content = editable?.innerHTML

      if (!editable && !content) return
      requestAnimationFrame(() => {
        if (
          editable &&
          (editable.innerHTML.trim() === '<br>' ||
            editable.innerHTML.trim() === '<br><br>')
        ) {
          editable.innerHTML = ''
        }
        const innerHTML = editable.innerHTML

        if (innerHTML?.length) {
          togglePlaceholderDisplay('none')
        }
        if (!innerHTML?.length) {
          togglePlaceholderDisplay('block')
        }
      })

      if (content?.trim()) {
        togglePlaceholderDisplay('none')
      } else {
        togglePlaceholderDisplay('block')
      }
    })

    useEffect(() => {
      if (!editableRef.current) return

      observer.observe(editableRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      })

      return () => {
        observer.disconnect()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    useImperativeHandle(ref, () => localRef.current as HTMLSpanElement)
    return (
      <span data-type="wrapperPlaceholder" ref={localRef} {...props}>
        <span
          {...placeholderProps}
          data-type="placeholder"
          ref={placeholderRef}
        >
          {description}
        </span>
        <span
          {...valueProps}
          contentEditable
          data-description={dataDescription}
          data-mask="input"
          ref={editableRef}
        />
      </span>
    )
  }
)
